import { useActivityPermissions } from "PFApp/activities/hooks";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { useActivityUpdate } from "PFCore/hooks/queries/activity";
import { useProfileInvalidate } from "PFCore/hooks/queries/profile";
import { useShortlistReset } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useGetColor } from "PFCore/hooks/use_get_color";
import { Activity, Shortlist } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import ButtonLabel from "../../../../activities/parts/button_label";
import ExceptionsWarning from "./exceptions_warning";
import css from "./shortlist_item_buttons.module.scss";

type BookedOrConfirmedButtonsProps = {
  activity: Activity;
  hasException: boolean;
  qaPrefix: string;
  isAudit: boolean;
  item: Shortlist;
};

const BookedOrConfirmedButtons = ({
  activity,
  hasException,
  qaPrefix,
  isAudit,
  item
}: BookedOrConfirmedButtonsProps) => {
  const { t } = useTranslation(["activities", "translation"]);
  const { invalidateProfileBookings } = useProfileInvalidate();
  const { update: updateActivity } = useActivityUpdate(activity?.id);
  const { mutate: resetShortlist } = useShortlistReset();
  const navigate = useNavigate();
  const getColor = useGetColor();

  const [displayUndo, setDisplayUndo] = useState(false);
  const { showGrowl } = useGrowlStoreActions();

  // used to refresh calendar on the side panel when undoing a booking
  const refreshBookings = async () => {
    await invalidateProfileBookings({ id: item.profile.id });
  };

  // can be only Confirmed or Booked
  const { state } = item;
  const { canReviewShortlists: canUndo } = useActivityPermissions(activity);

  const handleUndo = (event) => {
    event.stopPropagation();
    setDisplayUndo(true);
  };

  return (
    <>
      <div className={css.rolesActions}>
        <ButtonLabel>
          <Icon name="user-filled" size="sm" fill={getColor("palettePrimaryAction")} />
          {t(`activities:show.shortlist.state.${state}`)}
        </ButtonLabel>
        {canUndo && (
          <div className={css.acceptedButtons}>
            {hasException && <ExceptionsWarning />}
            <Button
              text={t("translation:undo")}
              kind="blank"
              disabled={hasException}
              onClick={handleUndo}
              qaId={`${qaPrefix}-undo`}
            />
          </div>
        )}
      </div>
      {displayUndo && (
        <Modal
          title={
            isAudit
              ? t("activities:show.shortlist.unallocateResource")
              : t("activities:show.shortlist.unfillRole")
          }
          labelOK={t("translation:continueText")}
          onOK={() => {
            resetShortlist(item.id, {
              onSuccess: () => {
                activity?.coowned && updateActivity({}); // this is needed to trigger refetching callback on audit planner,
                refreshBookings();
                showGrowl({
                  kind: "success",
                  message: isAudit
                    ? t("activities:show.shortlist.resourceUnallocated")
                    : t("activities:show.shortlist.roleUnfilled")
                });
                if (isAudit) {
                  navigate("/audit_planner");
                }
              },
              onError: () => activity?.coowned && updateActivity({})
            });
          }}
          onClose={() => {
            setDisplayUndo(false);
          }}
          kindOK="danger"
        >
          {isAudit
            ? t("activities:show.shortlist.wantUnallocateResource")
            : t("activities:show.shortlist.wantUnfillRole")}
        </Modal>
      )}
    </>
  );
};

export default BookedOrConfirmedButtons;
